import { Center, Grid, Input, PinInput, Stack, TextInput } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import { useMemo } from 'react';
import { PhoneNumberInput } from '../../../components/PhoneNumberInput';
import { FormBranchSelector } from '../../../components/form/FormBranchSelector';
import type { StaffPassword } from '../types/OperatorDto';

interface UpsertStaffProps<T> {
	form: UseFormReturnType<T, (values: T) => T>;
}

export function UpsertStaffGeneralInfo<T>({ form }: UpsertStaffProps<T>) {
	return (
		<Grid w={'100%'}>
			<Grid.Col span={12}>
				<TextInput
					label={'Username'}
					placeholder="Insert username..."
					key={form.key('username')}
					{...form.getInputProps('username')}
					data-cy="username-input"
				/>
			</Grid.Col>
			<Grid.Col span={6}>
				<TextInput
					label={'Card Number'}
					placeholder="Insert card number..."
					key={form.key('cardNo')}
					{...form.getInputProps('cardNo')}
					data-cy="cardNo-input"
				/>
			</Grid.Col>
			<Grid.Col span={6}>
				<FormBranchSelector form={form} property={'branchId'} />
			</Grid.Col>
			<Grid.Col span={12}>
				<TextInput
					label={'Job Title'}
					placeholder="Insert job title..."
					key={form.key('jobTitle')}
					{...form.getInputProps('jobTitle')}
					data-cy="jobTitle-input"
				/>
			</Grid.Col>
			<Grid.Col span={6}>
				<TextInput
					label={'First Name'}
					placeholder="Insert first name..."
					key={form.key('firstName')}
					{...form.getInputProps('firstName')}
					data-cy="firstName-input"
				/>
			</Grid.Col>
			<Grid.Col span={6}>
				<TextInput
					label={'Last Name'}
					placeholder="Insert last name..."
					key={form.key('surname')}
					{...form.getInputProps('surname')}
					data-cy="surname-input"
				/>
			</Grid.Col>
			<Grid.Col span={6}>
				<PhoneNumberInput
					label={'Phone Number'}
					key={form.key('phone')}
					{...form.getInputProps('phone')}
					data-cy="phone-input"
				/>
			</Grid.Col>
			<Grid.Col span={6}>
				<TextInput
					label={'Email'}
					placeholder={'Insert email'}
					key={form.key('email')}
					{...form.getInputProps('email')}
					data-cy="email-input"
				/>
			</Grid.Col>
		</Grid>
	);
}

export function UpsertStaffAddress<T>({ form }: UpsertStaffProps<T>) {
	return (
			<Grid w={'100%'}>
				<Grid.Col span={6}>
					<TextInput
						label={'Address Line One'}
						placeholder="Insert address line one..."
						key={form.key('addressLineOne')}
						{...form.getInputProps('addressLineOne')}
						data-cy="addressLineOne-input"
					/>
				</Grid.Col>
				<Grid.Col span={6}>
					<TextInput
						label={'Address Line Two'}
						placeholder="Insert address line two..."
						key={form.key('addressLineTwo')}
						{...form.getInputProps('addressLineTwo')}
						data-cy="addressLineTwo-input"
					/>
				</Grid.Col>
				<Grid.Col span={4}>
					<TextInput
						label={'Post Code'}
						placeholder="Insert post code..."
						key={form.key('postCode')}
						{...form.getInputProps('postCode')}
						data-cy="postCode-input"
					/>
				</Grid.Col>
				<Grid.Col span={4}>
					<TextInput
						label={'County'}
						placeholder="Insert county..."
						key={form.key('county')}
						{...form.getInputProps('county')}
						data-cy="county-input"
					/>
				</Grid.Col>
				<Grid.Col span={4}>
					<TextInput
						label={'Town'}
						placeholder="Insert town..."
						key={form.key('town')}
						{...form.getInputProps('town')}
						data-cy="town-input"
					/>
				</Grid.Col>
			</Grid>
	);
}

export function UpsertStaffEmergencyContact<T>({ form }: UpsertStaffProps<T>) {
	return (
			<Grid w={'100%'}>
				<Grid.Col span={6}>
					<TextInput
						label={'Contact Name'}
						placeholder="Insert emergency contact phone number..."
						key={form.key('emergencyContactName')}
						{...form.getInputProps('emergencyContactName')}
						data-cy="emergencyContactName-input"
					/>
				</Grid.Col>
				<Grid.Col span={6}>
					<PhoneNumberInput
						label={'Contact Phone Number'}
						key={form.key('emergencyContactNumber')}
						{...form.getInputProps('emergencyContactNumber')}
						data-cy="emergencyContactNumber-input"
					/>
				</Grid.Col>
			</Grid>
	);
}

export function UpsertStaffPassword({ form }: UpsertStaffProps<StaffPassword>) {
	const isPasswordInserted = useMemo(() => {
		const inputs = form.getValues();

		if (inputs.password.length !== 4) {
			form.setFieldValue('checkPassword', '');

			return false;
		}

		return true;
	}, [form]);

	return (
		<Grid w={'100%'}>
			<Center w={'100%'}>
				<Stack>
					<Input.Wrapper label={'Insert Password'}>
						<PinInput
							key={form.key('password')}
							{...form.getInputProps('password')}
							data-cy="password-input"
							type={'number'}
						/>
					</Input.Wrapper>
					<Input.Wrapper label={'Retype Password'}>
						<PinInput
							disabled={!isPasswordInserted}
							key={form.key('checkPassword')}
							{...form.getInputProps('checkPassword')}
							data-cy="checkPassword-input"
							type={'number'}
						/>
					</Input.Wrapper>
				</Stack>
			</Center>
		</Grid>
	);
}
